import axios from 'axios';
import config from '../config.json';

const COUNTRY_TO_LANG_MAP = {
  FR: 'fr', BE: 'fr', MC: 'fr',           // French
  DE: 'de', AT: 'de', CH: 'de',           // German
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', // Spanish
  CN: 'zh', TW: 'zh', HK: 'zh',           // Chinese
  SA: 'ar', AE: 'ar', EG: 'ar', QA: 'ar', KW: 'ar', OM: 'ar', BH: 'ar', // Arabic
  IN: 'en',                               // English (Default for India)
};

const SUPPORTED_LANGUAGES = ['en', 'fr', 'zh', 'de', 'es', 'hi', 'kn', 'ar'];

/**
 * Maps an ISO 3166-1 alpha-2 country code to a supported application language code.
 * @param {string} countryCode - e.g. "FR", "DE"
 * @returns {string} - e.g. "fr", "de", or default fallback "en"
 */
export const mapCountryToLanguage = (countryCode) => {
  if (!countryCode) return 'en';
  const matchedLang = COUNTRY_TO_LANG_MAP[countryCode.toUpperCase()];
  return SUPPORTED_LANGUAGES.includes(matchedLang) ? matchedLang : 'en';
};

// Set to null for real IP detection, or set to 'FR', 'DE', 'ES', 'SA' for testing.
// const TEST_MOCK_COUNTRY = null;

export const getDeviceIP = () => {
  return new Promise((resolve) => {
    try {
      const pc = new RTCPeerConnection({ iceServers: [] });
      pc.createDataChannel('');
      pc.createOffer().then((offer) => pc.setLocalDescription(offer));

      pc.onicecandidate = (event) => {
        if (!event || !event.candidate) return;
        const match = event.candidate.candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
        if (match) {
          pc.close();
          resolve(match[1]); // e.g. "10.203.188.233" or device LAN IP
        }
      };

      // Fallback timeout if no candidate is generated
      setTimeout(() => {
        try { pc.close(); } catch (e) { }
        resolve('127.0.0.1');
      }, 1000);
    } catch (err) {
      resolve('127.0.0.1');
    }
  });
};

// Step 2: Detect the region of the device without third-party APIs
export const getRegionFromDevice = async (deviceIp) => {
  // Option A: Check our own internal RouteEye backend endpoint (no 3rd-party)
  try {
    const response = await axios.get(
      `${config.baseURL}/appSettings/detect-location`,
      { params: { ip: deviceIp }, withCredentials: true, timeout: 2000 }
    );
    if (response?.data?.countryCode) {
      return response.data.countryCode;
    }
  } catch (backendErr) {
  }

  // Option B: Native device locale & timezone (100% pure JS, zero external network calls)
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timeZone.includes('Kolkata') || timeZone.includes('Calcutta')) return 'IN';
    if (timeZone.includes('Taipei') || timeZone.includes('Taiwan')) return 'TW';
    if (timeZone.includes('Paris')) return 'FR';
    if (timeZone.includes('Berlin')) return 'DE';
    if (timeZone.includes('Madrid')) return 'ES';
    if (timeZone.includes('Riyadh') || timeZone.includes('Dubai')) return 'SA';

    // Check browser native language locale (e.g. "zh-TW" -> "TW", "fr-FR" -> "FR")
    const navLocale = navigator.language || '';
    const parts = navLocale.split('-');
    if (parts.length > 1) {
      return parts[1].toUpperCase();
    }
  } catch (e) { }

  return 'IN';
};

export const detectUserLanguageByIP = async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const mockCountryParam = urlParams.get('country');
    if (mockCountryParam) {
      console.log(`[IP Detection Simulated via URL] Country: ${mockCountryParam}`);
      const mapped = mapCountryToLanguage(mockCountryParam);
      sessionStorage.setItem('session_detected_lang', mapped);
      return mapped;
    }
  } catch (paramErr) { }

  // if (TEST_MOCK_COUNTRY) {
  //   console.log(`[TEST MODE] IP Language Detection mocked for country: ${TEST_MOCK_COUNTRY}`);
  //   return mapCountryToLanguage(TEST_MOCK_COUNTRY);
  // }

  try {

    // Step 1: Find the IP address of the device
    const deviceIp = await getDeviceIP();

    // Step 2: Detect the region based on that device
    const region = await getRegionFromDevice(deviceIp);

    // Step 3: Map region to application language
    const language = mapCountryToLanguage(region);

    try {
      sessionStorage.setItem('session_detected_lang', language);
    } catch (e) { }

    return language;
  } catch (error) {
    console.warn('Device language detection failed, defaulting to "en":', error);
    return 'en';
  }
};
